import { makeAutoObservable } from 'mobx';
import createStoreContext from '../createStoreContext';
import {
  ReportGroupSummaryCacheEntry,
  ReportGroupSummaryCacheMap,
} from 'src/types/reportGroup';
import { REPORT_GROUP_SUMMARY_STORAGE_KEY } from 'src/constants/reportGroupSummary';

const EMPTY_SUMMARY_CACHE: ReportGroupSummaryCacheMap = {};

export const [ReportGroupSummaryStoreProvider, useReportGroupSummaryStore] =
  createStoreContext(
    class ReportGroupSummaryStore {
      private summaries: ReportGroupSummaryCacheMap = EMPTY_SUMMARY_CACHE;
      private isInitialized = false;

      public constructor() {
        makeAutoObservable(this);

        this.initializeFromStorage();
      }

      private initializeFromStorage() {
        if (this.isInitialized) {
          return;
        }

        if (typeof window === 'undefined') {
          this.summaries = {};
          this.isInitialized = true;

          return;
        }

        try {
          const storedValue = window.localStorage.getItem(
            REPORT_GROUP_SUMMARY_STORAGE_KEY,
          );

          if (storedValue) {
            const parsedValue: ReportGroupSummaryCacheMap =
              JSON.parse(storedValue);

            this.summaries = parsedValue;
          } else {
            this.summaries = {};
          }
        } catch {
          this.summaries = {};
        }

        this.isInitialized = true;
      }

      private persistSummaries() {
        if (typeof window === 'undefined') {
          return;
        }

        try {
          window.localStorage.setItem(
            REPORT_GROUP_SUMMARY_STORAGE_KEY,
            JSON.stringify(this.summaries),
          );
        } catch {
          // no-op
        }
      }

      getSummaryByKey = (
        cacheKey: string | null,
      ): ReportGroupSummaryCacheEntry | undefined => {
        if (!cacheKey) {
          return undefined;
        }

        this.initializeFromStorage();

        return this.summaries[cacheKey];
      };

      saveSummaryByKey = (
        cacheKey: string,
        entry: ReportGroupSummaryCacheEntry,
      ) => {
        this.initializeFromStorage();

        this.summaries = {
          ...this.summaries,
          [cacheKey]: entry,
        };

        this.persistSummaries();
      };

      clearSummaryByKey = (cacheKey: string | null) => {
        if (!cacheKey) {
          return;
        }

        this.initializeFromStorage();

        if (!this.summaries[cacheKey]) {
          return;
        }

        const nextSummaries: ReportGroupSummaryCacheMap = { ...this.summaries };

        delete nextSummaries[cacheKey];

        this.summaries = nextSummaries;
        this.persistSummaries();
      };

      resetCache = () => {
        this.summaries = {};
        this.persistSummaries();
      };
    },
  );

export type ReportGroupSummaryStore = ReturnType<
  typeof useReportGroupSummaryStore
>;
